import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import StatusPill, { STATUSES, STATUS_LABEL } from "../../components/StatusPill";
import ConversionWizard from "./ConversionWizard";
import {
  ArrowLeft, Mail, Phone, MapPin, Calendar, FileText, Trash2, Send,
  Download, Eye, AlertCircle, Loader2, Plus, CheckCircle2, UserCheck, Sparkles,
} from "lucide-react";

const TABS = ["application", "documents", "interviews", "notes", "history"];
const DOC_LABELS = {
  resume: "Resume", drivers_license: "Driver's License", state_id: "State ID",
  ssn_card: "SSN Card", tb_clearance: "TB Clearance", cpr: "CPR Certification",
  cna: "CNA Certification", guardian_registration: "Guardian Registration",
  vehicle_insurance: "Vehicle Insurance", vehicle_registration: "Vehicle Registration",
  other: "Other",
};

export default function ApplicantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [activeTab, setActiveTab] = useState("application");
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showConversionWizard, setShowConversionWizard] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/hiring/applications/${id}`);
      setApp(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (activeTab === "history") {
      api.get("/audit", { params: { limit: 200 } })
        .then((r) => setHistory((r.data || []).filter((l) => l.resource_id === id)))
        .catch(() => setHistory([]));
    }
  }, [activeTab, id]);

  const updateStatus = async (next) => {
    try {
      await api.patch(`/hiring/applications/${id}/status`, { status: next });
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const removeApp = async () => {
    if (!window.confirm("Permanently delete this application? Documents will be marked deleted.")) return;
    try {
      await api.delete(`/hiring/applications/${id}`);
      navigate("/hiring");
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (error && !app) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Link to="/hiring" className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <div className="surface p-6 text-red-700 bg-red-50 border-red-200">{error}</div>
      </div>
    );
  }
  if (!app) {
    return <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 inline animate-spin" /> Loading…</div>;
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-8 max-w-5xl mx-auto">
      <Link to="/hiring" className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-4 hover:text-foreground" data-testid="back-to-queue">
        <ArrowLeft className="w-4 h-4" /> Back to queue
      </Link>

      {/* Header card */}
      <div className="surface p-5 md:p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="heading-display" data-testid="applicant-name">{app.first_name} {app.last_name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{app.email}</span>
              <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{app.phone}</span>
              <span className="font-mono text-xs">{app.tracking_code}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={app.status} />
            {/* Convert to Employee button - only show for offer_accepted or onboarding */}
            {(app.status === "offer_accepted" || app.status === "onboarding") && (
              <button
                onClick={() => setShowConversionWizard(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="convert-to-employee-btn"
              >
                <UserCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Convert to Employee</span>
                <Sparkles className="w-3 h-3 sm:hidden" />
              </button>
            )}
            <select
              value={app.status}
              onChange={(e) => updateStatus(e.target.value)}
              className="input-base !w-auto !py-1.5 !px-3 text-xs"
              data-testid="status-select"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
            <button onClick={removeApp} className="btn-ghost text-red-600 hover:bg-red-50" data-testid="delete-application">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-border" data-testid="tab-bar">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-3 py-2 text-sm font-medium capitalize border-b-2 transition-colors -mb-px ${
              activeTab === t ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t}`}
            style={activeTab === t ? { borderColor: "hsl(var(--primary))", color: "hsl(var(--primary))" } : {}}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === "application" && <ApplicationPanel app={app} />}
      {activeTab === "documents" && <DocumentsPanel app={app} reload={load} />}
      {activeTab === "interviews" && <InterviewsPanel app={app} reload={load} />}
      {activeTab === "notes" && <NotesPanel app={app} reload={load} />}
      {activeTab === "history" && <HistoryPanel history={history} />}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-1 text-sm">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function Bool({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-1 text-sm">{value === true ? "Yes" : value === false ? "No" : <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function ApplicationPanel({ app }) {
  return (
    <div className="space-y-5">
      <div className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Email" value={app.email} />
          <Field label="Phone" value={app.phone} />
          <Field label="Address" value={[app.address, app.city, app.state, app.zip_code].filter(Boolean).join(", ")} />
          <Field label="Position" value={app.position_applied_for} />
        </div>
      </div>
      <div className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Caregiver Qualifications</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Bool label="Driver's License" value={app.has_drivers_license} />
          <Bool label="Reliable Transportation" value={app.has_reliable_transportation} />
          <Bool label="CNA Certification" value={app.has_cna_certification} />
          <Bool label="CPR Certification" value={app.has_cpr_certification} />
          <Bool label="TB Clearance" value={app.has_tb_clearance} />
          <Field label="Years of Experience" value={app.years_of_experience} />
        </div>
      </div>
      <div className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Availability & Background</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Available Days" value={(app.availability_days || []).join(", ")} />
          <Field label="Preferred Shift" value={app.availability_shift} />
          <Field label="Earliest Start Date" value={app.earliest_start_date} />
          <Field label="Previous Employer" value={app.previous_employer} />
          <div className="md:col-span-2">
            <Field label="Why home care?" value={app.why_home_care} />
          </div>
          <div className="md:col-span-2">
            <Field label="Additional info" value={app.additional_info} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentsPanel({ app, reload }) {
  const docs = app.documents || [];
  const remove = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/hiring/documents/${docId}`);
    reload();
  };
  const viewDoc = async (doc) => {
    if (doc.will_bring_to_office || !doc.storage_path) return;
    // Fetch as blob with credentials
    try {
      const resp = await api.get(`/files/${doc.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch {
      window.alert("Could not load file.");
    }
  };

  return (
    <div className="surface p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading-section">Documents ({docs.length})</h3>
      </div>
      {docs.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground text-sm">
          <FileText className="w-8 h-8 mx-auto mb-3 opacity-40" />
          No documents uploaded yet.
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between border border-border rounded-lg p-3" data-testid={`doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{DOC_LABELS[d.document_type] || d.document_type}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {d.will_bring_to_office ? "Will bring to office" : d.original_filename}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {d.will_bring_to_office ? (
                  <span className="pill-warning">Will bring</span>
                ) : (
                  <button onClick={() => viewDoc(d)} className="btn-ghost !px-2 !py-1" title="View"><Eye className="w-4 h-4" /></button>
                )}
                <button onClick={() => remove(d.id)} className="btn-ghost !px-2 !py-1 text-red-600" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InterviewsPanel({ app, reload }) {
  const [show, setShow] = useState(false);
  const [scheduled_at, setSchedAt] = useState("");
  const [duration_minutes, setDur] = useState(30);
  const [interview_type, setType] = useState("in_person");
  const [location, setLoc] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      await api.post(`/hiring/applications/${app.id}/interviews`, {
        application_id: app.id,
        scheduled_at: new Date(scheduled_at).toISOString(),
        duration_minutes: Number(duration_minutes),
        interview_type, location, notes,
      });
      setShow(false);
      setSchedAt(""); setDur(30); setType("in_person"); setLoc(""); setNotes("");
      reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const interviews = app.interviews || [];

  return (
    <div className="surface p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading-section">Interviews ({interviews.length})</h3>
        <button onClick={() => setShow((v) => !v)} className="btn-primary" data-testid="schedule-interview-button">
          <Plus className="w-4 h-4" /> Schedule
        </button>
      </div>

      {show && (
        <form onSubmit={submit} className="border border-border rounded-lg p-4 mb-4 bg-secondary/20 space-y-3" data-testid="schedule-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">When</label>
              <input type="datetime-local" required value={scheduled_at} onChange={(e) => setSchedAt(e.target.value)} className="input-base" data-testid="interview-when" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Duration (min)</label>
              <input type="number" min={5} max={300} value={duration_minutes} onChange={(e) => setDur(e.target.value)} className="input-base" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Type</label>
              <select value={interview_type} onChange={(e) => setType(e.target.value)} className="input-base">
                <option value="in_person">In Person</option>
                <option value="virtual">Virtual</option>
                <option value="phone">Phone</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Location / Link</label>
              <input value={location} onChange={(e) => setLoc(e.target.value)} className="input-base" placeholder="Office address or meeting link" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="input-base min-h-[60px]" />
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="schedule-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send & Schedule</>}
            </button>
          </div>
        </form>
      )}

      {interviews.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          <Calendar className="w-8 h-8 mx-auto mb-3 opacity-40" />
          No interviews scheduled.
        </div>
      ) : (
        <div className="space-y-2">
          {interviews.map((iv) => (
            <div key={iv.id} className="border border-border rounded-lg p-3" data-testid={`interview-${iv.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{new Date(iv.scheduled_at).toLocaleString()}</div>
                <span className="pill-info">{iv.interview_type.replace("_", " ")}</span>
              </div>
              {iv.location && <div className="text-xs text-muted-foreground mt-1">{iv.location}</div>}
              {iv.notes && <div className="text-xs text-foreground mt-2">{iv.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotesPanel({ app, reload }) {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const notes = app.notes || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/hiring/applications/${app.id}/notes`, {
        application_id: app.id,
        content: content.trim(),
      });
      setContent("");
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/hiring/notes/${noteId}`);
    reload();
  };

  return (
    <div className="surface p-5 md:p-6">
      <h3 className="heading-section mb-4">Internal Notes</h3>
      <form onSubmit={submit} className="mb-4" data-testid="add-note-form">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="input-base min-h-[80px]"
          placeholder="Add a note for the hiring team…"
          data-testid="note-input"
        />
        <div className="flex justify-end mt-2">
          <button type="submit" disabled={submitting || !content.trim()} className="btn-primary" data-testid="note-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Add note</>}
          </button>
        </div>
      </form>
      {notes.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6">No notes yet.</div>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="border border-border rounded-lg p-3" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium">{n.author_name}</div>
                <div className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              <div className="text-sm whitespace-pre-wrap">{n.content}</div>
              <div className="flex justify-end mt-1">
                <button onClick={() => remove(n.id)} className="text-xs text-red-600 hover:underline">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryPanel({ history }) {
  return (
    <div className="surface p-5 md:p-6">
      <h3 className="heading-section mb-4">Activity History</h3>
      {history.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6">No history yet.</div>
      ) : (
        <ol className="relative border-l border-border ml-2 space-y-3">
          {history.map((h) => (
            <li key={h.id} className="ml-4">
              <span className="absolute w-2 h-2 bg-foreground/30 rounded-full -left-[5px] mt-1.5" />
              <div className="text-sm font-medium">{h.action.replace(/_/g, " ").replace(/\./g, " · ")}</div>
              <div className="text-xs text-muted-foreground">
                {h.user_email || "system"} · {new Date(h.created_at).toLocaleString()}
              </div>
              {Object.keys(h.metadata || {}).length > 0 && (
                <div className="text-xs text-muted-foreground mt-1 font-mono">{JSON.stringify(h.metadata)}</div>
              )}
            </li>
          ))}
        </ol>
      )}

      {/* Conversion Wizard */}
      <ConversionWizard
        application={app}
        isOpen={showConversionWizard}
        onClose={() => setShowConversionWizard(false)}
        onSuccess={(result) => {
          // Navigate to new employee profile
          if (result?.employee_id) {
            navigate(`/employees/${result.employee_id}`);
          }
        }}
      />
    </div>
  );
}
